import { ArrowLeft, MailCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { api, unwrapVoid } from "@/api/client";
import { useMeta } from "@/api/meta";
import { Field } from "@/components/common/Field";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errorText } from "@/lib/errors";

import { AuthShell } from "./AuthShell";

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const meta = useMeta();
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);

  const back = (
    <Link to="/login" className="inline-flex items-center gap-1.5 text-primary hover:underline">
      <ArrowLeft className="size-4" aria-hidden="true" /> {t("forgot.back")}
    </Link>
  );

  if (meta.data && !meta.data.email_enabled) {
    return (
      <AuthShell title={t("forgot.title")} footer={back}>
        <Alert variant="warning">{t("forgot.noEmail")}</Alert>
      </AuthShell>
    );
  }

  if (sentTo) {
    return (
      <AuthShell title={t("forgot.sentTitle")} footer={back}>
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="flex size-12 items-center justify-center rounded-full bg-success-soft text-success">
            <MailCheck className="size-6" aria-hidden="true" />
          </span>
          <p role="status">{t("forgot.sentText", { email: sentTo })}</p>
        </div>
      </AuthShell>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      unwrapVoid(await api.POST("/api/v1/auth/password/forgot", { body: { email: email.trim() } }));
      setSentTo(email.trim());
    } catch (err) {
      setError(err);
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthShell title={t("forgot.title")} subtitle={t("forgot.subtitle")} footer={back}>
      <form className="flex flex-col gap-5" onSubmit={(e) => void submit(e)}>
        <Field label={t("login.email")}>
          <Input
            type="email"
            autoComplete="username"
            required
            autoFocus
            className="h-11"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        {error !== null && <Alert variant="danger">{errorText(t, error)}</Alert>}
        <Button type="submit" size="lg" disabled={pending || !email.includes("@")}>
          {t("forgot.submit")}
        </Button>
      </form>
    </AuthShell>
  );
}
